import { isValid, differenceInCalendarDays } from 'date-fns';
import {
  SortingField,
  SortingOrder,
  TrailerType,
  Requirement,
  BrokerId,
  DatabaseBroker,
  FrontendBroker,
  Place,
  LoadsSearchRequestV2,
  USStates,
  usStateAbbreviationToName,
  usStateNameToAbbreviation,
  stateToZone,
  ZipZone,
  zoneToStates,
  UUID,
  TrailerTypeAbbreviations,
  USStateFullNames,
} from '@trucksmarter/shared';
import {
  DEFAULT_TRAILER_TYPES,
  VALID_FILTERABLE_TRAILER_TYPE_OPTIONS,
} from '@ui/shared/trailer-types';
import {
  MAX_PICKUP_TIME_RANGE_NUM_DAYS,
  DEFAULT_SORT_FIELD,
  DEFAULT_SORT_ORDER,
  MIN_EQUIPMENT_LENGTH_FEET,
  MAX_EQUIPMENT_LENGTH_FEET,
  MIN_RATE_PER_MILE_AMOUNT,
  MAX_RATE_PER_MILE_AMOUNT,
  MIN_PRICE_AMOUNT,
  MAX_PRICE_AMOUNT,
  MIN_WEIGHT_POUNDS,
  MAX_WEIGHT_POUNDS,
  MIN_DISTANCE_MILES,
  MAX_DISTANCE_MILES,
  DEFAULT_REQUIREMENTS,
  DEFAULT_RADIUS_MILES,
  SUPPORTED_SORT_FIELD_OPTIONS,
  SUPPORTED_SORT_ORDER_OPTIONS,
  DEFAULT_PICKUP_TIME_RANGE_NUM_DAYS,
  LoadBoardSearchLocation,
} from '../_ui/LoadBoardSearchForm/loadboard-search-form-values';
import LoadsSearchParamName from './loads-search-param-name';
import LoadsSearchQuery from './loads-search-query';
import SelectLoadEntryPoint from '../../../_ui/SelectLoadEntryPointProvider/SelectLoadEntryPoint';
import LOADBOARD_SORT_OPTIONS from './loadboard-sort-options';

export function createUrlSearchParamsFromObject(
  object: object,
): URLSearchParams {
  const urlSearchParams = new URLSearchParams();

  Object.entries(object).forEach(([key, value]) => {
    if (value != null) {
      if (Array.isArray(value)) {
        value.forEach((value) => {
          urlSearchParams.append(key, `${value}`);
        });
      } else {
        urlSearchParams.set(key, `${value}`);
      }
    }
  });

  return urlSearchParams;
}

export function createLoadsSearchQueryFromURLSearchParams(
  urlSearchParams: URLSearchParams,
  decodeBase64ToJson: <T>(base64String: string) => T | null,
): LoadsSearchQuery {
  // Pickup time range
  const today = new Date();
  const day = getMaybeNanNumberFromSearchParams(
    urlSearchParams,
    LoadsSearchParamName.Day,
  );
  const year = getMaybeNanNumberFromSearchParams(
    urlSearchParams,
    LoadsSearchParamName.Year,
  );
  const month = getMaybeNanNumberFromSearchParams(
    urlSearchParams,
    LoadsSearchParamName.Month,
  );
  const hour = getMaybeNanNumberFromSearchParams(
    urlSearchParams,
    LoadsSearchParamName.Hour,
  );
  const minute = getMaybeNanNumberFromSearchParams(
    urlSearchParams,
    LoadsSearchParamName.Minute,
  );
  const numDays = Math.min(
    getMaybeNanNumberFromSearchParams(
      urlSearchParams,
      LoadsSearchParamName.NumDays,
      DEFAULT_PICKUP_TIME_RANGE_NUM_DAYS,
    ),
    MAX_PICKUP_TIME_RANGE_NUM_DAYS,
  );
  const searchParamsDate = new Date(year, month - 1, day);
  const startDate = isValid(searchParamsDate)
    ? differenceInCalendarDays(today, searchParamsDate) > 0
      ? today
      : searchParamsDate
    : today;
  // Sort
  const field =
    (urlSearchParams?.get(LoadsSearchParamName.Field) as SortingField) ??
    DEFAULT_SORT_FIELD;
  const order =
    (urlSearchParams?.get(LoadsSearchParamName.Order) as SortingOrder) ??
    DEFAULT_SORT_ORDER;
  // Filters
  const searchParamsTrailerTypes =
    urlSearchParams?.getAll(LoadsSearchParamName.TrailerTypes) ?? [];
  const trailerTypes = (
    searchParamsTrailerTypes.length
      ? searchParamsTrailerTypes
      : DEFAULT_TRAILER_TYPES
  ).filter(
    (trailerType) =>
      VALID_FILTERABLE_TRAILER_TYPE_OPTIONS[trailerType as TrailerType],
  ) as TrailerType[];
  const equipmentLength = getRangeFromSearchParams(
    urlSearchParams,
    LoadsSearchParamName.EquipmentLength,
    MIN_EQUIPMENT_LENGTH_FEET,
    MAX_EQUIPMENT_LENGTH_FEET,
  );
  const ratePerMile = getRangeFromSearchParams(
    urlSearchParams,
    LoadsSearchParamName.RatePerMile,
    MIN_RATE_PER_MILE_AMOUNT,
    MAX_RATE_PER_MILE_AMOUNT,
  );
  const price = getRangeFromSearchParams(
    urlSearchParams,
    LoadsSearchParamName.Price,
    MIN_PRICE_AMOUNT,
    MAX_PRICE_AMOUNT,
  );
  const weight = getRangeFromSearchParams(
    urlSearchParams,
    LoadsSearchParamName.Weight,
    MIN_WEIGHT_POUNDS,
    MAX_WEIGHT_POUNDS,
  );
  const distance = getRangeFromSearchParams(
    urlSearchParams,
    LoadsSearchParamName.Distance,
    MIN_DISTANCE_MILES,
    MAX_DISTANCE_MILES,
  );
  // Requirements
  const requirementsOnly = (
    urlSearchParams?.getAll(LoadsSearchParamName.RequirementsOnly) ?? []
  ).filter((requirement) => DEFAULT_REQUIREMENTS[requirement]) as Requirement[];
  const requirementsNever = (
    urlSearchParams?.getAll(LoadsSearchParamName.RequirementsNever) ?? []
  ).filter((requirement) => DEFAULT_REQUIREMENTS[requirement]) as Requirement[];
  // Locations
  const pickupLocations = getLocationsFromSearchParams(
    urlSearchParams,
    LoadsSearchParamName.PickupZone,
    LoadsSearchParamName.PickupState,
    LoadsSearchParamName.PickupBase64EncodedLocation,
    decodeBase64ToJson,
  );
  const pickupRadiusMiles = getMaybeNanNumberFromSearchParams(
    urlSearchParams,
    LoadsSearchParamName.PickupRadiusMiles,
    DEFAULT_RADIUS_MILES,
  );
  const deliveryLocations = getLocationsFromSearchParams(
    urlSearchParams,
    LoadsSearchParamName.DeliveryZone,
    LoadsSearchParamName.DeliveryState,
    LoadsSearchParamName.DeliveryBase64EncodedLocation,
    decodeBase64ToJson,
  );
  const deliveryRadiusMiles = getMaybeNanNumberFromSearchParams(
    urlSearchParams,
    LoadsSearchParamName.DeliveryRadiusMiles,
    DEFAULT_RADIUS_MILES,
  );
  // Brokers
  const brokerList =
    urlSearchParams?.getAll(LoadsSearchParamName.BrokerList) ?? [];
  const shouldExcludeBrokerList =
    urlSearchParams?.get(LoadsSearchParamName.ShouldExcludeBrokerList) != null;
  const searchParamIncludeMissingPriceLoads = urlSearchParams?.get(
    LoadsSearchParamName.IncludeMissingPriceLoads,
  );
  const includeMissingPriceLoads =
    searchParamIncludeMissingPriceLoads == null
      ? undefined
      : searchParamIncludeMissingPriceLoads === 'true';
  const excludeLoadId =
    (urlSearchParams?.get(LoadsSearchParamName.ExcludeLoadId) as UUID) ??
    undefined;

  return {
    pickupTimeRange: {
      startDay: {
        year: startDate.getFullYear(),
        month: startDate.getMonth() + 1,
        day: startDate.getDate(),
      },
      ...(!isNaN(hour) &&
        !isNaN(minute) && {
          startTime: {
            hour,
            minute,
          },
        }),
      numDays,
    },
    filters: {
      trailerTypes,
      equipmentLength,
      ratePerMile,
      price,
      weight,
      distance,
      [shouldExcludeBrokerList ? 'excludedBrokers' : 'includedBrokers']:
        brokerList,
    },
    options: {
      sort: {
        field: SUPPORTED_SORT_FIELD_OPTIONS.includes(field)
          ? field
          : DEFAULT_SORT_FIELD,
        order: SUPPORTED_SORT_ORDER_OPTIONS.includes(order)
          ? order
          : DEFAULT_SORT_ORDER,
      },
      includeMissingPriceLoads,
    },
    requirements: {
      only: requirementsOnly,
      never: requirementsNever,
    },
    pickupLocations,
    pickupRadiusMiles,
    deliveryLocations,
    deliveryRadiusMiles,
    excludeLoadId,
  };
}

export function getMaybeNanNumberFromSearchParams(
  urlSearchParams: URLSearchParams | null,
  name: LoadsSearchParamName,
  defaultValue?: number,
): number {
  return parseInt(
    urlSearchParams?.get(name)?.replace(/\D/g, '') || `${defaultValue ?? ''}`,
  );
}

export function getRangeFromSearchParams(
  urlSearchParams: URLSearchParams | null,
  name: LoadsSearchParamName,
  min: number,
  max: number,
): [number, number] {
  const [start, end] = (urlSearchParams?.getAll(name) ?? [])
    .map((value) => {
      const number = parseInt(value);
      return isNaN(number) ? null : number;
    })
    .sort((a, b) => {
      if (a === null || b === null) {
        return 0;
      }

      return a - b;
    });

  return [start ?? min, end ?? max];
}

export function getLocationsFromSearchParams(
  urlSearchParams: URLSearchParams | null,
  paramNameZone: string,
  paramNameState: string,
  base64EncodedLocationName: string,
  decodeBase64ToJson: <T>(base64String: string) => T | null,
): LoadBoardSearchLocation[] | null {
  const zones = (urlSearchParams?.getAll(paramNameZone) ?? []) as ZipZone[];
  const filteredZones = zones.filter((zone) => zone in zoneToStates);

  if (filteredZones.length) {
    return filteredZones.map(
      (zone: ZipZone): LoadBoardSearchLocation => ({
        placeId: `ggl-${zone}`,
        type: 'zone',
        state: zoneToStates[zone],
      }),
    );
  }

  const states = (urlSearchParams?.getAll(paramNameState) ?? []) as USStates[];
  const filteredStates = states.filter(
    (state) => !!usStateAbbreviationToName[state],
  );

  if (filteredStates.length) {
    return filteredStates.map((state) => ({
      type: 'state',
      state,
      placeId: `${state} - ${usStateAbbreviationToName[state]}`,
    }));
  }

  const base64EncodedLocation = urlSearchParams?.get(base64EncodedLocationName);

  if (!base64EncodedLocation) {
    return null;
  }

  const decodedLocation = decodeBase64ToJson<Place>(base64EncodedLocation);

  return decodedLocation ? [decodedLocation] : null;
}

export function createLoadBoardURLSearchParamsFromQuery(
  query: LoadsSearchQuery,
  brokers: Partial<Record<DatabaseBroker, FrontendBroker>>,
  encodeJsonToBase64: (json: object) => string | null,
  selectLoadEntryPoint: SelectLoadEntryPoint = SelectLoadEntryPoint.LoadBoard,
): URLSearchParams {
  const searchParams = new URLSearchParams();
  const excludedBrokers = query.filters?.excludedBrokers ?? [];
  const excludedBrokersMap = Object.fromEntries(
    excludedBrokers.map((brokerId) => [brokerId, true]),
  ) as Partial<Record<DatabaseBroker, boolean>>;
  const includedBrokers = Object.keys(brokers).filter(
    (brokerId) => !excludedBrokersMap[brokerId as BrokerId],
  ) as DatabaseBroker[];
  const shouldExcludeBrokerList =
    excludedBrokers.length <= includedBrokers.length;
  const brokerList = shouldExcludeBrokerList
    ? excludedBrokers
    : includedBrokers;

  // Pickup time range
  searchParams.set(
    LoadsSearchParamName.Day,
    `${query.pickupTimeRange.startDay.day}`,
  );
  searchParams.set(
    LoadsSearchParamName.Year,
    `${query.pickupTimeRange.startDay.year}`,
  );
  searchParams.set(
    LoadsSearchParamName.Month,
    `${query.pickupTimeRange.startDay.month}`,
  );
  searchParams.set(
    LoadsSearchParamName.NumDays,
    `${query.pickupTimeRange.numDays}`,
  );

  if (query.pickupTimeRange.startTime != null) {
    searchParams.set(
      LoadsSearchParamName.Hour,
      `${query.pickupTimeRange.startTime.hour}`,
    );
    searchParams.set(
      LoadsSearchParamName.Minute,
      `${query.pickupTimeRange.startTime.minute}`,
    );
  }

  // Options
  if (query.options?.sort?.field) {
    searchParams.set(LoadsSearchParamName.Field, query.options.sort.field);
  }

  if (query.options?.sort?.order) {
    searchParams.set(LoadsSearchParamName.Order, query.options.sort.order);
  }

  if (query.options?.includeMissingPriceLoads) {
    searchParams.set(LoadsSearchParamName.IncludeMissingPriceLoads, 'true');
  }

  // Filters
  query.filters?.trailerTypes?.forEach((trailerType) =>
    searchParams.append(LoadsSearchParamName.TrailerTypes, trailerType),
  );
  query.filters?.equipmentLength?.forEach((length) =>
    searchParams.append(
      LoadsSearchParamName.EquipmentLength,
      JSON.stringify(length),
    ),
  );
  query.filters?.ratePerMile?.forEach((rate) =>
    searchParams.append(LoadsSearchParamName.RatePerMile, JSON.stringify(rate)),
  );
  query.filters?.price?.forEach((price) =>
    searchParams.append(LoadsSearchParamName.Price, JSON.stringify(price)),
  );
  query.filters?.weight?.forEach((weight) =>
    searchParams.append(LoadsSearchParamName.Weight, JSON.stringify(weight)),
  );
  query.filters?.distance?.forEach((distance) =>
    searchParams.append(
      LoadsSearchParamName.Distance,
      JSON.stringify(distance),
    ),
  );

  // Requirements
  query.requirements?.only?.forEach((requirement) =>
    searchParams.append(LoadsSearchParamName.RequirementsOnly, requirement),
  );
  query.requirements?.never?.forEach((requirement) =>
    searchParams.append(LoadsSearchParamName.RequirementsNever, requirement),
  );

  // Pickup & Delivery locations
  [
    {
      locations: query.pickupLocations,
      radiusMiles: query.pickupRadiusMiles,
      searchParamNameBase64EncodedLocation:
        LoadsSearchParamName.PickupBase64EncodedLocation,
      searchParamNameRadiusMiles: LoadsSearchParamName.PickupRadiusMiles,
      searchParamNameState: LoadsSearchParamName.PickupState,
      searchParamNameZone: LoadsSearchParamName.PickupZone,
    },
    {
      locations: query.deliveryLocations,
      radiusMiles: query.deliveryRadiusMiles,
      searchParamNameBase64EncodedLocation:
        LoadsSearchParamName.DeliveryBase64EncodedLocation,
      searchParamNameRadiusMiles: LoadsSearchParamName.DeliveryRadiusMiles,
      searchParamNameState: LoadsSearchParamName.DeliveryState,
      searchParamNameZone: LoadsSearchParamName.DeliveryZone,
    },
  ].forEach(
    ({
      locations,
      searchParamNameBase64EncodedLocation,
      searchParamNameRadiusMiles,
      searchParamNameState,
      searchParamNameZone,
    }) => {
      if (locations !== null) {
        if (locations[0].type === 'city') {
          const base64EncodedPickupLocation = encodeJsonToBase64(locations[0]);

          if (base64EncodedPickupLocation !== null) {
            searchParams.set(
              searchParamNameBase64EncodedLocation,
              base64EncodedPickupLocation,
            );
            searchParams.set(
              searchParamNameRadiusMiles,
              `${query.pickupRadiusMiles}`,
            );
          }
        } else if (locations[0].type === 'state') {
          locations.forEach(({ state }) => {
            if (typeof state === 'string') {
              searchParams.append(searchParamNameState, state);
            }
          });
        } else if (locations[0].type === 'zone') {
          locations.forEach(({ state }) => {
            if (state) {
              searchParams.append(
                searchParamNameZone,
                stateToZone[
                  (typeof state === 'string' ? state : state[0]) as USStates
                ],
              );
            }
          });
        }
      }
    },
  );

  // Brokers
  brokerList.forEach((brokerId) =>
    searchParams.append(LoadsSearchParamName.BrokerList, brokerId),
  );

  if (shouldExcludeBrokerList) {
    searchParams.set(LoadsSearchParamName.ShouldExcludeBrokerList, 'true');
  }

  if (query.excludeLoadId) {
    searchParams.set(LoadsSearchParamName.ExcludeLoadId, query.excludeLoadId);
  }

  // Select Load Entry Point
  if (selectLoadEntryPoint !== SelectLoadEntryPoint.LoadBoard) {
    searchParams.set(
      LoadsSearchParamName.SelectLoadEntryPoint,
      selectLoadEntryPoint,
    );
  }

  return searchParams;
}

export function createLoadsSearchRequestFromQuery(
  query: LoadsSearchQuery,
): LoadsSearchRequestV2 {
  const {
    pickupLocations,
    pickupRadiusMiles,
    deliveryLocations,
    deliveryRadiusMiles,
    filters,
    ...baseLoadsSearchRequest
  } = query;

  return {
    ...baseLoadsSearchRequest,
    filters: {
      ...filters,
      price: [
        filters?.price?.[0] || null,
        filters?.price?.[1] != null
          ? filters.price[1] >= MAX_PRICE_AMOUNT
            ? null
            : filters.price[1]
          : null,
      ],
      distance: [
        filters?.distance?.[0] || null,
        filters?.distance?.[1] != null
          ? filters.distance[1] >= MAX_DISTANCE_MILES
            ? null
            : filters.distance[1]
          : null,
      ],
      weight: [
        filters?.weight?.[0] || null,
        filters?.weight?.[1] != null
          ? filters.weight[1] >= MAX_WEIGHT_POUNDS
            ? null
            : filters.weight[1]
          : null,
      ],
      equipmentLength: [
        filters?.equipmentLength?.[0] || null,
        filters?.equipmentLength?.[1] != null
          ? filters.equipmentLength[1] >= MAX_EQUIPMENT_LENGTH_FEET
            ? null
            : filters.equipmentLength[1]
          : null,
      ],
      ratePerMile: [
        filters?.ratePerMile?.[0] || null,
        filters?.ratePerMile?.[1] != null
          ? filters.ratePerMile[1] >= MAX_RATE_PER_MILE_AMOUNT
            ? null
            : filters.ratePerMile[1]
          : null,
      ],
    },
    pickupSearchLocation:
      pickupLocations !== null
        ? pickupLocations[0].type === 'zone'
          ? {
              state: pickupLocations.reduce(
                (states, { state }) =>
                  state
                    ? ([
                        ...states,
                        ...(Array.isArray(state) ? state : [state]),
                      ] as USStates[])
                    : states,
                [] as USStates[],
              ),
            }
          : pickupLocations[0].type === 'state' && pickupLocations[0].state
          ? {
              state:
                usStateNameToAbbreviation[
                  pickupLocations[0].state as USStateFullNames
                ] ?? (pickupLocations.map(({ state }) => state) as USStates[]),
            }
          : pickupLocations[0].type === 'city' &&
            pickupLocations[0].latitude &&
            pickupLocations[0].longitude
          ? {
              latitude: pickupLocations[0].latitude,
              longitude: pickupLocations[0].longitude,
              radiusMiles: pickupRadiusMiles,
            }
          : undefined
        : undefined,
    deliverySearchLocation:
      deliveryLocations !== null
        ? deliveryLocations[0].type === 'zone'
          ? {
              state: deliveryLocations.reduce(
                (states, { state }) =>
                  state
                    ? ([
                        ...states,
                        ...(Array.isArray(state) ? state : [state]),
                      ] as USStates[])
                    : states,
                [] as USStates[],
              ),
            }
          : deliveryLocations[0].type === 'state' && deliveryLocations[0].state
          ? {
              state:
                usStateNameToAbbreviation[
                  deliveryLocations[0].state as USStateFullNames
                ] ??
                (deliveryLocations.map(({ state }) => state) as USStates[]),
            }
          : deliveryLocations[0].type === 'city' &&
            deliveryLocations[0].latitude &&
            deliveryLocations[0].longitude
          ? {
              latitude: deliveryLocations[0].latitude,
              longitude: deliveryLocations[0].longitude,
              radiusMiles: deliveryRadiusMiles,
            }
          : undefined
        : undefined,
  };
}

export function isValidLoadBoardSearchParams(
  searchParams: URLSearchParams,
  decodeBase64ToJson: <T>(base64String: string) => T | null,
): boolean {
  return (
    hasValidFilters(searchParams) &&
    hasValidSortOptions(searchParams) &&
    hasValidPickupTimeRange(searchParams) &&
    hasPickupLocations(searchParams, decodeBase64ToJson)
  );
}

function hasValidFilters(searchParams: URLSearchParams): boolean {
  return (
    searchParams
      .getAll(LoadsSearchParamName.TrailerTypes)
      .filter((trailerType) => trailerType in TrailerTypeAbbreviations).length >
      0 &&
    searchParams
      .getAll(LoadsSearchParamName.EquipmentLength)
      .filter((length) => !isNaN(parseInt(length))).length === 2 &&
    searchParams
      .getAll(LoadsSearchParamName.RatePerMile)
      .filter((rpm) => !isNaN(parseFloat(rpm))).length === 2 &&
    searchParams
      .getAll(LoadsSearchParamName.Price)
      .filter((price) => !isNaN(parseFloat(price))).length === 2 &&
    searchParams
      .getAll(LoadsSearchParamName.Weight)
      .filter((weight) => !isNaN(parseInt(weight))).length === 2 &&
    searchParams
      .getAll(LoadsSearchParamName.Distance)
      .filter((distance) => !isNaN(parseInt(distance))).length === 2
  );
}

function hasValidPickupTimeRange(searchParams: URLSearchParams): boolean {
  const yearString = searchParams.get(LoadsSearchParamName.Year);
  const monthString = searchParams.get(LoadsSearchParamName.Month);
  const dayString = searchParams.get(LoadsSearchParamName.Day);
  const numDaysString = searchParams.get(LoadsSearchParamName.NumDays);

  if (
    yearString != null &&
    monthString != null &&
    dayString != null &&
    numDaysString != null
  ) {
    const year = parseInt(yearString);
    const month = parseInt(monthString);
    const day = parseInt(dayString);
    const numDays = parseInt(numDaysString);

    if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(numDays)) {
      return false;
    }

    return isValid(new Date(year, month, day));
  }

  return false;
}

const validSortOptions = LOADBOARD_SORT_OPTIONS.map(
  ({ value: { field, order } }) => `${field}-${order}`,
);

function hasValidSortOptions(searchParams: URLSearchParams): boolean {
  return validSortOptions.includes(
    `${searchParams.get(LoadsSearchParamName.Field)}-${searchParams.get(
      LoadsSearchParamName.Order,
    )}`,
  );
}

function hasPickupLocations(
  searchParams: URLSearchParams,
  decodeBase64ToJson: <T>(base64String: string) => T | null,
): boolean {
  if (searchParams.has(LoadsSearchParamName.PickupZone)) {
    return (
      !searchParams.has(LoadsSearchParamName.PickupState) &&
      !searchParams.has(LoadsSearchParamName.PickupBase64EncodedLocation) &&
      searchParams
        .getAll(LoadsSearchParamName.PickupZone)
        .filter((zone) => zone in zoneToStates).length > 0
    );
  }

  if (searchParams.has(LoadsSearchParamName.PickupState)) {
    return (
      !searchParams.has(LoadsSearchParamName.PickupZone) &&
      !searchParams.has(LoadsSearchParamName.PickupBase64EncodedLocation) &&
      searchParams
        .getAll(LoadsSearchParamName.PickupState)
        .filter((state) => state in USStates).length > 0
    );
  }

  if (
    searchParams.has(LoadsSearchParamName.PickupBase64EncodedLocation) &&
    !searchParams.has(LoadsSearchParamName.PickupZone) &&
    !searchParams.has(LoadsSearchParamName.PickupState) &&
    searchParams.has(LoadsSearchParamName.PickupRadiusMiles)
  ) {
    const encodedLocation = searchParams.get(
      LoadsSearchParamName.PickupBase64EncodedLocation,
    );

    if (encodedLocation == null) {
      return false;
    }

    const decodedLocation = decodeBase64ToJson<Place>(encodedLocation);

    return decodedLocation != null;
  }

  return false;
}
